import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../config/api';
import { handleUnauthorized } from '../../services/api';
import { FolderOpen, Search, Plus, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { clearCache, CACHE_KEYS } from '../../services/cacheService';
import { useTranslation, Trans } from 'react-i18next';

interface Category {
  id: number;
  name: string;
  description: string | null;
  documents_count: number;
  created_at: string;
}

interface PaginationMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

const emptyForm = { name: '', description: '' };

const AdminCategories: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState<PaginationMeta | null>(null);

  // Form dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Delete dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page on search change
  useEffect(() => { setCurrentPage(1); }, [debouncedSearch]);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
      params.set('page', currentPage.toString());

      const res = await fetch(`${API_BASE_URL}/admin/categories?${params}`, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!res.ok) {
        if (res.status === 401) { handleUnauthorized(); return; }
        throw new Error(`Error ${res.status}`);
      }
      const data = await res.json();
      setCategories(data.data || []);
      setPaginationMeta({
        current_page: data.current_page,
        last_page: data.last_page,
        per_page: data.per_page,
        total: data.total,
      });
    } catch (err: any) {
      setError(err.message || t('common:errors.generic'));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, currentPage, t]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  // Open create dialog
  const handleCreate = () => {
    setEditingCategory(null);
    setForm(emptyForm);
    setFormErrors({});
    setFormOpen(true);
  };

  // Open edit dialog
  const handleEdit = (cat: Category) => {
    setEditingCategory(cat);
    setForm({ name: cat.name, description: cat.description || '' });
    setFormErrors({});
    setFormOpen(true);
  };

  // Submit form
  const handleSubmit = async () => {
    setSaving(true);
    setFormErrors({});
    try {
      const isEdit = !!editingCategory;
      const url = isEdit
        ? `${API_BASE_URL}/admin/categories/${editingCategory!.id}`
        : `${API_BASE_URL}/admin/categories`;

      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        if (res.status === 401) { handleUnauthorized(); return; }
        const data = await res.json();
        if (data.errors) {
          const errs: Record<string, string> = {};
          Object.entries(data.errors).forEach(([k, v]) => {
            errs[k] = Array.isArray(v) ? v[0] : String(v);
          });
          setFormErrors(errs);
        } else {
          throw new Error(data.message || t('common:errors.generic'));
        }
        return;
      }

      setFormOpen(false);
      // Clear cached categories so other parts of the app fetch fresh data
      clearCache(CACHE_KEYS.CATEGORIES);
      fetchCategories();
    } catch (err: any) {
      setFormErrors({ _general: err.message });
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDeleteClick = (cat: Category) => {
    setCategoryToDelete(cat);
    setDeleteError(null);
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/categories/${categoryToDelete.id}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 401) { handleUnauthorized(); return; }
        setDeleteError(data.message || t('common:errors.generic'));
        return;
      }
      setDeleteOpen(false);
      setCategoryToDelete(null);
      clearCache(CACHE_KEYS.CATEGORIES);
      fetchCategories();
    } catch (err: any) {
      setDeleteError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="container mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between gap-4 items-start lg:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('admin:categories.page_title')}</h1>
          <p className="text-muted-foreground">{t('admin:categories.page_subtitle')}</p>
        </div>
        <div className="flex flex-row gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('admin:categories.search_placeholder')}
              className="pl-9 w-64"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" /> {t('admin:categories.new_category')}
          </Button>
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-primary rounded-full border-t-transparent" />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">
              <p>{error}</p>
              <Button variant="outline" onClick={fetchCategories} className="mt-4">{t('common:actions.try_again')}</Button>
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">{t('admin:categories.no_categories_found')}</h3>
              <p className="text-muted-foreground">{t('admin:categories.no_categories_hint')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('admin:categories.table_name')}</TableHead>
                    <TableHead>{t('admin:categories.table_description')}</TableHead>
                    <TableHead className="text-center">{t('admin:categories.table_actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.map((cat) => (
                    <TableRow key={cat.id} className="cursor-pointer hover:bg-muted/50" onClick={() => handleEdit(cat)}>
                      <TableCell className="font-medium">{cat.name}</TableCell>
                      <TableCell className="max-w-[300px] truncate text-muted-foreground">
                        {cat.description || '—'}
                      </TableCell>
                      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-8 w-8 p-0 ${cat.documents_count > 0 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-red-50'}`}
                          title={cat.documents_count > 0 ? t('admin:categories.cannot_delete_has_documents') : t('common:actions.delete')}
                          onClick={() => cat.documents_count === 0 && handleDeleteClick(cat)}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {paginationMeta && paginationMeta.last_page > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-muted-foreground">
                {t('admin:categories.showing_categories', {
                  start: ((paginationMeta.current_page - 1) * paginationMeta.per_page) + 1,
                  end: Math.min(paginationMeta.current_page * paginationMeta.per_page, paginationMeta.total),
                  total: paginationMeta.total,
                })}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>
                  <ChevronLeft className="h-4 w-4" /> {t('common:pagination.previous')}
                </Button>
                <span className="text-sm font-medium">
                  {t('common:pagination.page_of', { current: paginationMeta.current_page, last: paginationMeta.last_page })}
                </span>
                <Button variant="outline" size="sm" disabled={currentPage >= paginationMeta.last_page} onClick={() => setCurrentPage((p) => Math.min(paginationMeta.last_page, p + 1))}>
                  {t('common:pagination.next')} <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCategory ? t('admin:categories.edit_category') : t('admin:categories.create_category')}</DialogTitle>
            <DialogDescription>
              {editingCategory ? t('admin:categories.edit_description') : t('admin:categories.create_description')}
            </DialogDescription>
          </DialogHeader>

          {formErrors._general && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{formErrors._general}</div>
          )}

          <div className="grid gap-4 py-2">
            <div>
              <Label htmlFor="cat_name">{t('admin:categories.form_name')} *</Label>
              <Input id="cat_name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              {formErrors.name && <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>}
            </div>
            <div>
              <Label htmlFor="cat_desc">{t('admin:categories.form_description')}</Label>
              <Textarea id="cat_desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
              {formErrors.description && <p className="text-xs text-red-500 mt-1">{formErrors.description}</p>}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)} disabled={saving}>{t('common:actions.cancel')}</Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving ? t('common:actions.saving') : editingCategory ? t('common:actions.update') : t('common:actions.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin:categories.delete_category')}</DialogTitle>
            <DialogDescription>
              <Trans
                i18nKey="admin:categories.delete_confirm"
                values={{ name: categoryToDelete?.name }}
                components={{ strong: <strong /> }}
              />
            </DialogDescription>
          </DialogHeader>
          {deleteError && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded">{deleteError}</div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={deleting}>{t('common:actions.cancel')}</Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={deleting}>
              {deleting ? t('common:actions.deleting') : t('common:actions.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCategories;
